import {
    Box,
    CircularProgress,
    Divider,
    Stack,
    Typography
} from '@mui/material'
import PropTypes from 'prop-types'

/**
 * The frame every peek shares: what kind of record this is, its name, a line
 * of identifiers, actions, then sections. Keeping it common is what makes a
 * location and a meter feel like parts of one system.
 */
export default function PeekLayout({
    kind,
    title,
    subtitle,
    actions,
    children
}) {
    return (
        <Box sx={{ p: 3, pt: 2.5 }}>
            <Box sx={{ pr: 6 }}>
                <Typography variant="overline" color="text.secondary">
                    {kind}
                </Typography>
                <Typography
                    variant="h5"
                    component="h2"
                    sx={{ wordBreak: 'break-word' }}
                >
                    {title}
                </Typography>
                {subtitle ? (
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mt: 0.5 }}
                    >
                        {subtitle}
                    </Typography>
                ) : null}
            </Box>
            {actions ? (
                <Stack
                    direction="row"
                    spacing={1}
                    flexWrap="wrap"
                    useFlexGap
                    sx={{ mt: 2 }}
                >
                    {actions}
                </Stack>
            ) : null}
            <Stack spacing={3} divider={<Divider />} sx={{ mt: 3 }}>
                {children}
            </Stack>
        </Box>
    )
}

PeekLayout.propTypes = {
    actions: PropTypes.node,
    children: PropTypes.node,
    kind: PropTypes.string.isRequired,
    subtitle: PropTypes.node,
    title: PropTypes.node.isRequired
}

/** A titled block within a peek. */
export function PeekSection({ title, children }) {
    return (
        <Box component="section">
            <Typography variant="subtitle2" component="h3" sx={{ mb: 1 }}>
                {title}
            </Typography>
            {children}
        </Box>
    )
}

PeekSection.propTypes = {
    children: PropTypes.node,
    title: PropTypes.node.isRequired
}

/** Label/value pairs, as a description list. */
export function PeekDetails({ rows }) {
    return (
        <Box
            component="dl"
            sx={{
                display: 'grid',
                gridTemplateColumns: 'minmax(110px, auto) 1fr',
                columnGap: 2,
                rowGap: 1,
                m: 0
            }}
        >
            {rows.filter(Boolean).map(({ label, value }) => (
                <Box key={label} sx={{ display: 'contents' }}>
                    <Typography
                        component="dt"
                        variant="body2"
                        color="text.secondary"
                    >
                        {label}
                    </Typography>
                    <Typography component="dd" variant="body2" sx={{ m: 0 }}>
                        {value}
                    </Typography>
                </Box>
            ))}
        </Box>
    )
}

PeekDetails.propTypes = {
    rows: PropTypes.array.isRequired
}

/** Loading and not-found states, in the same frame. */
export function PeekPlaceholder({ kind, loading }) {
    return (
        <PeekLayout kind={kind} title={loading ? 'Loading…' : 'Not found'}>
            {loading ? (
                <CircularProgress size={24} aria-label="Loading" />
            ) : (
                <Typography variant="body2" color="text.secondary">
                    It may have been removed, or belong to a different
                    organisation from the one you have open.
                </Typography>
            )}
        </PeekLayout>
    )
}

PeekPlaceholder.propTypes = {
    kind: PropTypes.string.isRequired,
    loading: PropTypes.bool
}
